import {
  Board,
  BoardCreate,
  BoardUpdate,
  BoardMember,
  ItemGroup,
  ItemGroupCreate,
  ItemGroupUpdate,
  Item,
  ItemCreate,
  ItemUpdate,
  PaginatedResponse,
  ListItemsParams,
  BoardSummary,
  BoardMemberRole,
  ItemDependency,
  ItemDependencyCreate,
  ItemDependencies,
  BoardDependencyLink,
  ItemChecklistItem,
  ItemChecklistItemCreate,
  ItemChecklistItemUpdate,
  ChecklistProgress,
} from '../types';
import { apiGet, apiPost, apiPut, apiDelete } from '../../../shared/api/axiosInstance';
import type { DeletionImpact } from '../../../generated/api/models/deletionImpact';

const API_BASE = '/api/v1/tasks';

// ============== Boards ==============

export interface ListBoardsParams {
  workspace_id?: string;
  no_workspace?: boolean;
  status?: string;
  search?: string;
  my_boards?: boolean;
  tags?: string;
  tags_mode?: 'any' | 'all';
  limit?: number;
  offset?: number;
}

export const boardApi = {
  list: async (params?: ListBoardsParams): Promise<PaginatedResponse<Board>> => {
    const searchParams = new URLSearchParams();
    if (params?.workspace_id) searchParams.set('workspace_id', params.workspace_id);
    if (params?.no_workspace) searchParams.set('no_workspace', 'true');
    if (params?.status) searchParams.set('status', params.status);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.my_boards) searchParams.set('my_boards', 'true');
    if (params?.tags) searchParams.set('tags', params.tags);
    if (params?.tags_mode) searchParams.set('tags_mode', params.tags_mode);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const url = `${API_BASE}/boards${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<PaginatedResponse<Board>>(url);
  },

  /**
   * List boards shared with the user where they don't have workspace access.
   * These are "orphaned" shared boards.
   */
  listShared: async (params?: { limit?: number; offset?: number }): Promise<PaginatedResponse<Board>> => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const url = `${API_BASE}/boards/shared${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<PaginatedResponse<Board>>(url);
  },

  getBoardSummaries: async (params: {
    workspace_id?: string;
    board_ids?: string[];
    show_completed?: boolean;
  } & ListItemsParams): Promise<Record<string, BoardSummary>> => {
    const searchParams = new URLSearchParams();
    if (params.workspace_id) searchParams.set('workspace_id', params.workspace_id);
    if (params.board_ids?.length) searchParams.set('board_ids', params.board_ids.join(','));
    if (params.statuses?.length) searchParams.set('status', params.statuses.join(','));
    if (params.priorities?.length) searchParams.set('priority', params.priorities.join(','));
    if (params.assigned_to_ids?.length) searchParams.set('assigned_to_ids', params.assigned_to_ids.join(','));
    if (params.search) searchParams.set('search', params.search);
    if (params.due_date_from) searchParams.set('due_date_from', params.due_date_from);
    if (params.due_date_to) searchParams.set('due_date_to', params.due_date_to);
    if (params.sprint_id !== undefined && params.sprint_id !== null) {
      searchParams.set('sprint_id', params.sprint_id === 'backlog' ? 'null' : params.sprint_id);
    }
    if (params.show_completed !== undefined) searchParams.set('show_completed', String(params.show_completed));

    const url = `${API_BASE}/boards/summaries${searchParams.toString() ? `?${searchParams}` : ''}`;
    const result = await apiGet<{ data: Record<string, BoardSummary> }>(url);
    return result.data;
  },

  get: async (
    id: string,
    options?: { includeGroups?: boolean; includeMembers?: boolean }
  ): Promise<{ data: Board }> => {
    const searchParams = new URLSearchParams();
    if (options?.includeGroups) searchParams.set('include_groups', 'true');
    if (options?.includeMembers) searchParams.set('include_members', 'true');

    const url = `${API_BASE}/boards/${id}${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<{ data: Board }>(url);
  },

  create: async (data: BoardCreate): Promise<{ data: Board }> => {
    return apiPost<{ data: Board }>(`${API_BASE}/boards`, data);
  },

  update: async (id: string, data: BoardUpdate): Promise<{ data: Board }> => {
    return apiPut<{ data: Board }>(`${API_BASE}/boards/${id}`, data);
  },

  delete: async (id: string, options?: { deleteItems?: boolean }): Promise<void> => {
    const params = new URLSearchParams();
    if (options?.deleteItems) {
      params.set('delete_tasks', 'true');
    }
    const url = `${API_BASE}/boards/${id}${params.toString() ? `?${params}` : ''}`;
    await apiDelete(url);
  },

  getDeletionImpact: async (id: string): Promise<DeletionImpact> => {
    // Shared DeletionImpact preview (deletion-and-cascade-design §5.1): the
    // delete_tasks keep/remove decision + the default-board blocker.
    return apiGet<DeletionImpact>(`${API_BASE}/boards/${id}/deletion-impact`);
  },

  addMember: async (
    boardId: string,
    userId: string,
    role: BoardMemberRole
  ): Promise<{ data: BoardMember }> => {
    return apiPost<{ data: BoardMember }>(`${API_BASE}/boards/${boardId}/members`, {
      user_id: userId,
      role,
    });
  },

  removeMember: async (boardId: string, userId: string): Promise<void> => {
    await apiDelete(`${API_BASE}/boards/${boardId}/members/${userId}`);
  },

  reorder: async (workspaceId: string | null, boardIds: string[]): Promise<{ success: boolean }> => {
    return apiPut<{ success: boolean }>(`${API_BASE}/boards/reorder`, {
      workspace_id: workspaceId,
      board_ids: boardIds,
    });
  },

  /** Batch-fetch blocking dependencies among a board's tasks (for the Gantt view). */
  listDependencies: async (boardId: string): Promise<{ data: BoardDependencyLink[] }> => {
    return apiGet<{ data: BoardDependencyLink[] }>(`${API_BASE}/boards/${boardId}/dependencies`);
  },
};

// ============== Groups ==============

export const groupApi = {
  listByBoard: async (
    boardId: string,
    options?: { includeItems?: boolean }
  ): Promise<{ data: ItemGroup[] }> => {
    const searchParams = new URLSearchParams();
    if (options?.includeItems) searchParams.set('include_tasks', 'true');

    const url = `${API_BASE}/boards/${boardId}/groups${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<{ data: ItemGroup[] }>(url);
  },

  create: async (boardId: string, data: ItemGroupCreate): Promise<{ data: ItemGroup }> => {
    return apiPost<{ data: ItemGroup }>(`${API_BASE}/boards/${boardId}/groups`, data);
  },

  update: async (groupId: string, data: ItemGroupUpdate): Promise<{ data: ItemGroup }> => {
    return apiPut<{ data: ItemGroup }>(`${API_BASE}/groups/${groupId}`, data);
  },

  delete: async (groupId: string): Promise<void> => {
    await apiDelete(`${API_BASE}/groups/${groupId}`);
  },

  reorder: async (boardId: string, groupIds: string[]): Promise<void> => {
    await apiPut(`${API_BASE}/boards/${boardId}/groups/reorder`, { group_ids: groupIds });
  },
};

// ============== Items ==============

export const taskApi = {
  listByBoard: async (
    boardId: string,
    params?: ListItemsParams
  ): Promise<PaginatedResponse<Item>> => {
    const searchParams = new URLSearchParams();
    if (params?.group_id) searchParams.set('group_id', params.group_id);
    if (params?.sprint_id !== undefined) {
      // 'backlog' means items with no sprint (sprint_id IS NULL)
      if (params.sprint_id === 'backlog') {
        searchParams.set('sprint_id', 'null');
      } else if (params.sprint_id) {
        searchParams.set('sprint_id', params.sprint_id);
      }
    }
    if (params?.statuses?.length) searchParams.set('status', params.statuses.join(','));
    if (params?.priorities?.length) searchParams.set('priority', params.priorities.join(','));
    // Support both legacy single assignee and new multi-select
    if (params?.assigned_to_ids?.length) {
      searchParams.set('assigned_to_ids', params.assigned_to_ids.join(','));
    } else if (params?.assigned_to) {
      searchParams.set('assigned_to', params.assigned_to);
    }
    if (params?.search) searchParams.set('search', params.search);
    if (params?.due_date_from) searchParams.set('due_date_from', params.due_date_from);
    if (params?.due_date_to) searchParams.set('due_date_to', params.due_date_to);
    if (params?.tags) searchParams.set('tags', params.tags);
    if (params?.tags_mode) searchParams.set('tags_mode', params.tags_mode);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const url = `${API_BASE}/boards/${boardId}/tasks${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<PaginatedResponse<Item>>(url);
  },

  listMyItems: async (params?: {
    statuses?: string[];
    priorities?: string[];
    board_id?: string;
    due_date_from?: string;
    due_date_to?: string;
    search?: string;
    sprint_id?: string;
    tags?: string;
    tags_mode?: 'any' | 'all';
    limit?: number;
    offset?: number;
  }): Promise<{ data: Item[] }> => {
    const searchParams = new URLSearchParams();
    if (params?.statuses?.length) searchParams.set('status', params.statuses.join(','));
    if (params?.priorities?.length) searchParams.set('priority', params.priorities.join(','));
    if (params?.board_id) searchParams.set('board_id', params.board_id);
    if (params?.due_date_from) searchParams.set('due_date_from', params.due_date_from);
    if (params?.due_date_to) searchParams.set('due_date_to', params.due_date_to);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.sprint_id) searchParams.set('sprint_id', params.sprint_id);
    if (params?.tags) searchParams.set('tags', params.tags);
    if (params?.tags_mode) searchParams.set('tags_mode', params.tags_mode);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const url = `${API_BASE}/my-tasks${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<{ data: Item[] }>(url);
  },

  listCrossBoard: async (params?: {
    assigned_to_me?: boolean;
    statuses?: string[];
    priorities?: string[];
    due_date_from?: string;
    due_date_to?: string;
    search?: string;
    sprint_id?: string;
    show_completed?: boolean;
    workspace_id?: string;
    completed_from?: string;
    completed_to?: string;
    include_orphaned?: boolean;
    include_subtasks?: boolean;
    tags?: string;
    tags_mode?: 'any' | 'all';
    limit?: number;
    offset?: number;
  }): Promise<{ data: Item[] }> => {
    const searchParams = new URLSearchParams();
    if (params?.assigned_to_me) searchParams.set('assigned_to_me', 'true');
    if (params?.statuses?.length) searchParams.set('status', params.statuses.join(','));
    if (params?.priorities?.length) searchParams.set('priority', params.priorities.join(','));
    if (params?.due_date_from) searchParams.set('due_date_from', params.due_date_from);
    if (params?.due_date_to) searchParams.set('due_date_to', params.due_date_to);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.sprint_id) searchParams.set('sprint_id', params.sprint_id);
    if (params?.show_completed === false) searchParams.set('show_completed', 'false');
    if (params?.workspace_id) searchParams.set('workspace_id', params.workspace_id);
    if (params?.completed_from) searchParams.set('completed_from', params.completed_from);
    if (params?.completed_to) searchParams.set('completed_to', params.completed_to);
    if (params?.include_orphaned) searchParams.set('include_orphaned', 'true');
    if (params?.include_subtasks) searchParams.set('include_subtasks', 'true');
    if (params?.tags) searchParams.set('tags', params.tags);
    if (params?.tags_mode) searchParams.set('tags_mode', params.tags_mode);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const url = `${API_BASE}/cross-board${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<{ data: Item[] }>(url);
  },

  listOrphaned: async (params?: {
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<Item>> => {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.set('search', params.search);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const url = `${API_BASE}/orphaned/list${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<PaginatedResponse<Item>>(url);
  },

  countOrphaned: async (): Promise<number> => {
    const data = await apiGet<{ count: number }>(`${API_BASE}/orphaned/count`);
    return data.count ?? 0;
  },

  get: async (
    id: string,
    options?: {
      includeSubitems?: boolean;
      includeAssignees?: boolean;
      includeWatchers?: boolean;
    }
  ): Promise<{ data: Item }> => {
    const searchParams = new URLSearchParams();
    if (options?.includeSubitems) searchParams.set('include_subitems', 'true');
    if (options?.includeAssignees) searchParams.set('include_assignees', 'true');
    if (options?.includeWatchers) searchParams.set('include_watchers', 'true');

    const url = `${API_BASE}/${id}${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<{ data: Item }>(url);
  },

  create: async (boardId: string | null, data: ItemCreate): Promise<{ data: Item }> => {
    if (boardId) {
      return apiPost<{ data: Item }>(`${API_BASE}/boards/${boardId}/tasks`, data);
    }
    return apiPost<{ data: Item }>(`${API_BASE}/quick-create`, data);
  },

  bulkCreate: async (
    boardId: string,
    data: {
      titles: string[];
      group_id?: string | null;
      parent_task_id?: string | null;
      status?: string;
      priority?: string;
      sprint_id?: string | null;
    }
  ): Promise<{ data: Item[]; created: number }> => {
    return apiPost<{ data: Item[]; created: number }>(
      `${API_BASE}/boards/${boardId}/tasks/bulk`,
      data
    );
  },

  update: async (id: string, data: ItemUpdate): Promise<{ data: Item }> => {
    return apiPut<{ data: Item }>(`${API_BASE}/${id}`, data);
  },

  delete: async (id: string): Promise<void> => {
    await apiDelete(`${API_BASE}/${id}`);
  },

  /**
   * Place an item directly before another item in the ordering.
   * This is the primary reorder operation for drag-and-drop.
   */
  placeBefore: async (id: string, targetId: string): Promise<{ data: Item }> => {
    return apiPut<{ data: Item }>(`${API_BASE}/${id}/place-before/${targetId}`, {});
  },

  /**
   * Place an item directly after another item in the ordering.
   * This is the primary reorder operation for drag-and-drop.
   */
  placeAfter: async (id: string, targetId: string): Promise<{ data: Item }> => {
    return apiPut<{ data: Item }>(`${API_BASE}/${id}/place-after/${targetId}`, {});
  },

  /**
   * Place an item at the end of its sibling list.
   * Used when dropping on an empty area or at the end of a list/column.
   */
  placeAtEnd: async (id: string): Promise<{ data: Item }> => {
    return apiPut<{ data: Item }>(`${API_BASE}/${id}/place-at-end`, {});
  },

  moveToParent: async (
    id: string,
    parentItemId: string | null
  ): Promise<{ data: Item }> => {
    return apiPut<{ data: Item }>(`${API_BASE}/${id}/move-to-parent`, {
      parent_task_id: parentItemId,
    });
  },

  getChildren: async (id: string): Promise<{ data: Item[]; count: number }> => {
    return apiGet<{ data: Item[]; count: number }>(`${API_BASE}/${id}/children`);
  },

  setAssignees: async (taskId: string, userIds: string[]): Promise<{ data: Item }> => {
    return apiPut<{ data: Item }>(`${API_BASE}/${taskId}/assignees`, { user_ids: userIds });
  },

  watch: async (taskId: string): Promise<void> => {
    await apiPost(`${API_BASE}/${taskId}/watch`);
  },

  unwatch: async (taskId: string): Promise<void> => {
    await apiDelete(`${API_BASE}/${taskId}/watch`);
  },
};

// Task comments migrated onto the unified entity comment system; use the shared
// commentsApi / <EntityComments entityType="task"> instead of a Tasks-specific API.

// ============== Dependencies ==============

export const dependencyApi = {
  list: async (taskId: string): Promise<{ data: ItemDependencies }> => {
    return apiGet<{ data: ItemDependencies }>(`${API_BASE}/${taskId}/dependencies`);
  },

  create: async (taskId: string, data: ItemDependencyCreate): Promise<{ data: ItemDependency }> => {
    return apiPost<{ data: ItemDependency }>(`${API_BASE}/${taskId}/dependencies`, data);
  },

  delete: async (dependencyId: string): Promise<void> => {
    await apiDelete(`${API_BASE}/dependencies/${dependencyId}`);
  },
};

// ============== Checklists ==============

export const checklistApi = {
  list: async (taskId: string): Promise<{ data: ItemChecklistItem[]; progress: ChecklistProgress }> => {
    return apiGet<{ data: ItemChecklistItem[]; progress: ChecklistProgress }>(`${API_BASE}/${taskId}/checklist`);
  },

  create: async (taskId: string, data: ItemChecklistItemCreate): Promise<{ data: ItemChecklistItem }> => {
    return apiPost<{ data: ItemChecklistItem }>(`${API_BASE}/${taskId}/checklist`, data);
  },

  bulkCreate: async (taskId: string, titles: string[]): Promise<{ data: ItemChecklistItem[] }> => {
    return apiPost<{ data: ItemChecklistItem[] }>(`${API_BASE}/${taskId}/checklist/bulk`, { titles });
  },

  update: async (checklistItemId: string, data: ItemChecklistItemUpdate): Promise<{ data: ItemChecklistItem }> => {
    return apiPut<{ data: ItemChecklistItem }>(`${API_BASE}/checklist/${checklistItemId}`, data);
  },

  toggle: async (checklistItemId: string): Promise<{ data: ItemChecklistItem }> => {
    return apiPost<{ data: ItemChecklistItem }>(`${API_BASE}/checklist/${checklistItemId}/toggle`);
  },

  reorder: async (taskId: string, checklistItemIds: string[]): Promise<void> => {
    await apiPut(`${API_BASE}/${taskId}/checklist/reorder`, { task_ids: checklistItemIds });
  },

  delete: async (checklistItemId: string): Promise<void> => {
    await apiDelete(`${API_BASE}/checklist/${checklistItemId}`);
  },
};
